import {
  CheckCircle2,
  CircleDashed,
  FileText,
  Handshake,
  Layers3,
  Mic,
  RefreshCw,
  ShieldCheck,
  Upload
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface ApiHealth {
  ok: boolean;
  service: string;
  productionUrl: string;
  checks: Record<string, boolean>;
}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api";

const milestones = [
  { id: "M0", label: "基盤", state: "active", detail: "Pages / Workers / D1 / R2" },
  { id: "M1", label: "認証", state: "next", detail: "Google OAuthと組織基盤" },
  { id: "M2", label: "資料", state: "next", detail: "作成・編集・アップロード" },
  { id: "M3", label: "確認", state: "next", detail: "参加・進行・承認状況" },
  { id: "M4", label: "生成", state: "next", detail: "資料中心の議事録生成" },
  { id: "M5", label: "承認", state: "next", detail: "相互承認の記録" }
];

const flow = [
  {
    title: "会議資料",
    body: "資料を作成、編集、アップロードして会議情報の根拠を残す。",
    icon: FileText
  },
  {
    title: "確認フォーム",
    body: "例外がある時だけ参加、進行、承認状況の説明を入力する。",
    icon: CheckCircle2
  },
  {
    title: "議事録生成",
    body: "資料と補足確認を主入力にして、必要に応じて音声を補助にする。",
    icon: Layers3
  },
  {
    title: "相互承認",
    body: "誰がいつ承認したかを記録し、後から追跡できるようにする。",
    icon: Handshake
  }
];

export function App() {
  const [health, setHealth] = useState<ApiHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  const completedChecks = useMemo(() => {
    if (!health) {
      return 0;
    }

    return Object.values(health.checks).filter(Boolean).length;
  }, [health]);

  async function loadHealth() {
    setHealthError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/health`);
      if (!response.ok) {
        throw new Error(`API responded with ${response.status}`);
      }

      setHealth((await response.json()) as ApiHealth);
    } catch (error) {
      setHealth(null);
      setHealthError(error instanceof Error ? error.message : "API health check failed");
    }
  }

  useEffect(() => {
    void loadHealth();
  }, []);

  return (
    <main className="app-shell">
      <aside className="sidebar" aria-label="Primary">
        <div className="brand">
          <div className="brand-mark">g</div>
          <div>
            <strong>gijify</strong>
            <span>MVP Console</span>
          </div>
        </div>

        <nav className="nav-list">
          <a className="nav-item active" href="#overview">
            <Layers3 size={18} />
            概要
          </a>
          <a className="nav-item" href="#materials">
            <FileText size={18} />
            会議資料
          </a>
          <a className="nav-item" href="#audio">
            <Mic size={18} />
            音声
          </a>
          <a className="nav-item" href="#approval">
            <ShieldCheck size={18} />
            承認
          </a>
        </nav>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">https://gijify.meta-task.ai</p>
            <h1>会議情報を少ない労力で共有し、議事録と承認記録を残す</h1>
          </div>
          <button className="icon-button" type="button" onClick={loadHealth} aria-label="Refresh API health">
            <RefreshCw size={18} />
          </button>
        </header>

        <section className="status-grid" id="overview">
          <div className="status-panel">
            <div className="panel-heading">
              <span>API Status</span>
              <strong>{health?.ok ? "Ready" : "Local setup"}</strong>
            </div>
            {health ? (
              <div className="health-row">
                <CheckCircle2 size={26} />
                <div>
                  <strong>{completedChecks} / {Object.keys(health.checks).length} bindings</strong>
                  <span>{health.service}</span>
                </div>
              </div>
            ) : (
              <div className="health-row muted">
                <CircleDashed size={26} />
                <div>
                  <strong>API worker not connected</strong>
                  <span>{healthError ?? "Start the Worker on port 8787"}</span>
                </div>
              </div>
            )}
          </div>

          <div className="status-panel">
            <div className="panel-heading">
              <span>Current Phase</span>
              <strong>M0</strong>
            </div>
            <p>
              Cloudflareアプリ基盤、D1 migration、Worker API、Pages frontendを接続する段階です。
            </p>
          </div>
        </section>

        <section className="flow-grid" aria-label="MVP flow">
          {flow.map((item) => {
            const Icon = item.icon;
            return (
              <article className="flow-card" key={item.title}>
                <div className="card-icon">
                  <Icon size={20} />
                </div>
                <h2>{item.title}</h2>
                <p>{item.body}</p>
              </article>
            );
          })}
        </section>

        <section className="work-panel">
          <div className="panel-heading">
            <span>Development Plan</span>
            <strong>M0 - M5</strong>
          </div>
          <div className="timeline">
            {milestones.map((milestone) => (
              <div className={`timeline-item ${milestone.state}`} key={milestone.id}>
                <div className="timeline-marker">{milestone.id}</div>
                <div>
                  <strong>{milestone.label}</strong>
                  <span>{milestone.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="action-row">
          <button className="primary-button" type="button">
            <FileText size={18} />
            会議資料を作成
          </button>
          <button className="secondary-button" type="button">
            <Upload size={18} />
            資料をアップロード
          </button>
          <button className="secondary-button" type="button">
            <Handshake size={18} />
            承認履歴
          </button>
        </section>
      </section>
    </main>
  );
}

